// CS Import — the large document viewer (prototype design). Shows the ORIGINAL
// document saved from commonsku (its PDF, or commonsku's own page), never a
// re-creation, with the project context and related records alongside.
// Read-only: open / download only.
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Download, X } from "lucide-react";
import { api } from "@/lib/api";
import { CS_DOC_LABEL, csDay, csMoney, type CsDocument } from "@/lib/csArchive";
import { MissingNote, PreviewButton } from "./parts";

export function CsDocumentViewer({ id, onOpen, onClose }: { id: string; onOpen: (id: string) => void; onClose: () => void }) {
  const q = useQuery({ queryKey: ["cs-archive", "document", id], queryFn: () => api<CsDocument>(`/cs-archive/documents/${id}`) });
  const [view, setView] = useState<"pdf" | "page" | "lines">("pdf");
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const d = q.data?.document;
  const o = q.data?.original;
  useEffect(() => {
    if (!o) return;
    setView(o.pdf?.url ? "pdf" : o.page?.url ? "page" : "lines");
  }, [o]);
  const label = d ? `${CS_DOC_LABEL[d.doc_type] ?? d.doc_type} ${d.number}` : "Document";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-brown-500/40 p-3 md:p-8" role="dialog" aria-modal="true" aria-label={label} onClick={onClose}>
      <div className="flex h-full max-h-[96vh] w-full max-w-[1500px] flex-col overflow-hidden rounded-xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <header className="flex items-center justify-between gap-4 border-b border-rule px-6 py-4">
          <div className="min-w-0">
            <p className="truncate text-[18px] font-semibold text-ink">{label}</p>
            <p className="truncate text-[14px] text-muted">{d ? `${d.client_name} / ${d.project_name} · #${d.job_number}` : "Loading…"}</p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {o?.pdf?.download_url ? <a href={o.pdf.download_url} className="inline-flex h-10 items-center gap-1.5 rounded-md border border-rule px-3 text-[14px] font-medium text-ink hover:bg-canvas"><Download className="h-4 w-4" aria-hidden />PDF</a> : null}
            {o?.page?.download_url ? <a href={o.page.download_url} className="inline-flex h-10 items-center gap-1.5 rounded-md border border-rule px-3 text-[14px] font-medium text-ink hover:bg-canvas"><Download className="h-4 w-4" aria-hidden />Page</a> : null}
            <button type="button" onClick={onClose} className="inline-flex h-10 items-center gap-1.5 rounded-md border border-rule px-3 text-[14px] font-medium text-ink hover:bg-canvas"><X className="h-4 w-4" aria-hidden />Close</button>
          </div>
        </header>
        <div className="flex min-h-0 flex-1 flex-col md:flex-row">
          <div className="flex min-h-0 flex-1 flex-col bg-canvas">
            <div className="flex gap-1 border-b border-rule bg-white px-4 py-2 text-[14px]">
              {(["pdf", "page", "lines"] as const).map((v) => (
                <button key={v} type="button" onClick={() => setView(v)} className={`rounded-md px-3 py-1.5 font-medium ${view === v ? "bg-ink text-white" : "text-ink hover:bg-canvas"}`}>
                  {v === "pdf" ? "Original PDF" : v === "page" ? "commonsku page" : "Lines & details"}
                </button>
              ))}
            </div>
            <div className="min-h-0 flex-1 overflow-auto">
              {q.isLoading ? <p className="p-8 text-muted">Loading the original…</p> : null}
              {q.isError ? <p className="p-8 text-red-700">This document couldn't be loaded. Try again.</p> : null}
              {d && view === "pdf" ? (o?.pdf?.url ? <iframe title={`${label} PDF`} src={o.pdf.url} className="h-full min-h-[70vh] w-full bg-white" /> : <NoOriginal state={o?.state} docType={d.doc_type} />) : null}
              {d && view === "page" ? (o?.page?.url ? <iframe title={`${label} — commonsku page`} src={o.page.url} sandbox="" className="h-full min-h-[70vh] w-full bg-white" /> : <NoOriginal state={o?.state} docType={d.doc_type} />) : null}
              {d && view === "lines" ? <Lines doc={q.data!} /> : null}
            </div>
          </div>
          <aside className="w-full shrink-0 overflow-auto border-t border-rule p-6 md:w-[340px] md:border-l md:border-t-0">
            <p className="text-[12px] font-medium uppercase tracking-[0.12em] text-muted">Document context</p>
            <p className="mt-1 text-[19px] font-semibold text-ink">Part of the whole story.</p>
            {d ? (
              <dl className="mt-4 space-y-3 text-[15px]">
                <Ctx label="Source">commonsku · project #{d.job_number}</Ctx>
                <Ctx label="Document type">{CS_DOC_LABEL[d.doc_type] ?? d.doc_type}</Ctx>
                <Ctx label="Number · date">{d.number} · {csDay(d.doc_date)}</Ctx>
                <Ctx label="Status in commonsku">{d.status ?? "—"}</Ctx>
                {d.total != null ? <Ctx label="Total">{csMoney(d.total, d.currency ?? "USD")}</Ctx> : null}
                {d.supplier_name ? <Ctx label="Supplier">{d.supplier_name}</Ctx> : null}
                <Ctx label="Client">{d.client_name}</Ctx>
                {d.contact_name ? <Ctx label="Contact">{d.contact_name}</Ctx> : null}
                <Ctx label="Historical rep">{[d.client_rep_name, d.order_rep_name && d.order_rep_name !== d.client_rep_name ? `${d.order_rep_name} (order rep)` : null].filter(Boolean).join(" · ") || "—"}</Ctx>
                <Ctx label="Record access">Read-only historical record</Ctx>
              </dl>
            ) : null}
            {q.data ? (
              <>
                <h4 className="mt-7 text-[16px] font-semibold text-ink">Approval evidence</h4>
                {q.data.approvals.length ? (
                  <ul className="mt-2 space-y-2 text-[14px]">
                    {q.data.approvals.slice(0, 8).map((a) => (
                      <li key={a.id} className="rounded-md bg-canvas px-3 py-2"><span className="text-muted">{a.occurred_at ? csDay(a.occurred_at) : "—"}{a.actor_name ? ` · ${a.actor_name}` : ""}</span><br /><span className="text-ink">{(a.text ?? "").slice(0, 220)}</span></li>
                    ))}
                  </ul>
                ) : <p className="mt-2 text-[14px] text-muted">No approval recorded in commonsku for this document.</p>}
                {q.data.related.length ? (
                  <>
                    <h4 className="mt-7 text-[16px] font-semibold text-ink">Related records</h4>
                    <div className="mt-2 space-y-2">
                      {q.data.related.map((r) => (
                        <button key={r.id} type="button" onClick={() => onOpen(r.id)} className="flex w-full items-center justify-between rounded-md border border-rule px-3 py-2.5 text-left text-[15px] text-ink hover:bg-canvas">
                          <span>{CS_DOC_LABEL[r.doc_type] ?? r.doc_type} {r.number}</span><span className="text-[13px] text-muted">{csDay(r.doc_date)} ↗</span>
                        </button>
                      ))}
                    </div>
                  </>
                ) : null}
              </>
            ) : null}
          </aside>
        </div>
      </div>
    </div>
  );
}

function Ctx({ label, children }: { label: string; children: React.ReactNode }) {
  return <div><dt className="text-[13px] text-muted">{label}</dt><dd className="text-ink">{children}</dd></div>;
}

function NoOriginal({ state, docType }: { state?: string; docType: string }) {
  const why = docType === "bill" ? "commonsku keeps supplier bills as data only — there is no original document to show."
    : state === "missing" ? "commonsku had no original for this document."
    : state === "failed" ? "The original couldn't be saved during import (see the Import log)."
    : "The original hasn't been saved yet.";
  return <div className="p-8"><MissingNote>Original not available</MissingNote><p className="mt-3 text-[15px] text-muted">{why} The recorded lines and details are under “Lines & details”.</p></div>;
}

function Lines({ doc }: { doc: CsDocument }) {
  const items = doc.items.filter((i) => i.kind !== "SEPARATOR");
  return (
    <div className="bg-white p-6">
      <p className="mb-4 text-[14px] text-muted">The details commonsku recorded for this document (not a re-creation of the document).</p>
      <table className="w-full text-left text-[14px]">
        <thead className="border-b border-rule text-muted"><tr><th className="py-2 pr-3 font-medium">Item</th><th className="py-2 pr-3 font-medium">Supplier</th><th className="py-2 pr-3 text-right font-medium">Qty</th><th className="py-2 pr-3 text-right font-medium">Cost</th><th className="py-2 pr-3 text-right font-medium">Price</th><th className="py-2 text-right font-medium">Total</th></tr></thead>
        <tbody>
          {items.map((i) => (
            <tr key={i.id} className={`border-b border-rule align-top ${i.hidden ? "text-muted" : "text-ink"}`}>
              <td className="py-2.5 pr-3">
                <span className="font-medium">{i.name ?? "—"}</span>{i.sku ? <span className="text-muted"> · {i.sku}</span> : null}{i.hidden ? <span className="ml-1 text-[12px]">(hidden from client)</span> : null}
                {i.decoration?.length ? <div className="mt-1 text-[13px] text-muted">{i.decoration.map((x, n) => <div key={n}>{[x.location, x.imprint, x.logo, x.color].filter(Boolean).join(" · ")}</div>)}</div> : null}
              </td>
              <td className="py-2.5 pr-3">{i.supplier_name ?? "—"}</td>
              <td className="py-2.5 pr-3 text-right">{i.quantity ? Number(i.quantity).toLocaleString() : "—"}</td>
              <td className="py-2.5 pr-3 text-right">{i.unit_cost != null ? csMoney(i.unit_cost) : "—"}</td>
              <td className="py-2.5 pr-3 text-right">{i.unit_price != null ? csMoney(i.unit_price) : "—"}</td>
              <td className="py-2.5 text-right">{csMoney(i.total)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {!items.length ? <p className="py-6 text-muted">No lines were recorded.</p> : null}
      {doc.artwork.length ? (
        <>
          <h4 className="mt-8 text-[16px] font-semibold text-ink">Artwork on this document</h4>
          <ul className="mt-2 space-y-2 text-[14px]">
            {doc.artwork.map((a) => (
              <li key={a.id} className="flex items-center justify-between gap-3 rounded-md border border-rule px-3 py-2">
                <span>{[a.title, a.item_name, a.location, a.imprint, a.color].filter(Boolean).join(" · ")}</span>
                {a.file?.url ? <PreviewButton f={a.file} /> : <MissingNote>{a.file ? "File not copied" : "No file in commonsku"}</MissingNote>}
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </div>
  );
}
